import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  UpdateCertificateRequestMutationRequest,
  UpdateCertificateRequestMutationResponse,
  UpdateCertificateRequestPathParams,
} from "../types/UpdateCertificateRequest"

type UpdateCertificateRequestClient = typeof client<
  UpdateCertificateRequestMutationResponse,
  Error,
  UpdateCertificateRequestMutationRequest
>
type UpdateCertificateRequest = {
  data: UpdateCertificateRequestMutationResponse
  error: Error
  request: UpdateCertificateRequestMutationRequest
  pathParams: UpdateCertificateRequestPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<UpdateCertificateRequestClient>>
  client: {
    parameters: Partial<Parameters<UpdateCertificateRequestClient>[0]>
    return: Awaited<ReturnType<UpdateCertificateRequestClient>>
  }
}
/**
 * @summary Update certificate request
 * @link /v1/certificate-request/update-certificate-request/:id
 */
export function useUpdateCertificateRequest(
  id: UpdateCertificateRequestPathParams["id"],
  options: {
    mutation?: UseMutationOptions<
      UpdateCertificateRequest["response"],
      UpdateCertificateRequest["error"],
      UpdateCertificateRequest["request"]
    >
    client?: UpdateCertificateRequest["client"]["parameters"]
  } = {}
): UseMutationResult<
  UpdateCertificateRequest["response"],
  UpdateCertificateRequest["error"],
  UpdateCertificateRequest["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    UpdateCertificateRequest["response"],
    UpdateCertificateRequest["error"],
    UpdateCertificateRequest["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        UpdateCertificateRequest["data"],
        UpdateCertificateRequest["error"],
        UpdateCertificateRequest["request"]
      >({
        method: "post",
        url: `/v1/certificate-request/update-certificate-request/${id}`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
